import React, { useState, useEffect } from 'react';
import { Eye, EyeOff, Check, X, AlertTriangle } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Progress } from '@/components/ui/progress';
import { checkPasswordStrength } from '@/lib/validation';

interface PasswordInputProps {
  id?: string;
  name?: string;
  placeholder?: string;
  value: string;
  onChange: (value: string) => void;
  onValidationChange?: (isValid: boolean) => void;
  showStrengthMeter?: boolean;
  required?: boolean;
  disabled?: boolean;
  className?: string;
  label?: string;
}

export const PasswordInput = React.forwardRef<HTMLInputElement, PasswordInputProps>(
  ({ 
    id,
    name,
    placeholder = 'Enter password',
    value,
    onChange,
    onValidationChange,
    showStrengthMeter = true,
    required = false,
    disabled = false,
    className,
    label = 'Password',
    ...props
  }, ref) => {
    const [showPassword, setShowPassword] = useState(false);
    const [strength, setStrength] = useState(checkPasswordStrength(''));

    useEffect(() => {
      const newStrength = checkPasswordStrength(value);
      setStrength(newStrength);
      onValidationChange?.(newStrength.isValid);
    }, [value, onValidationChange]);

    const getStrengthColor = (score: number) => {
      if (score <= 2) return 'bg-destructive';
      if (score <= 4) return 'bg-yellow-500';
      return 'bg-green-500';
    };

    const getStrengthText = (score: number) => {
      if (score <= 2) return 'Weak';
      if (score <= 4) return 'Medium';
      return 'Strong';
    };

    return (
      <div className="space-y-2">
        {label && (
          <Label htmlFor={id}>
            {label}
            {required && <span className="text-destructive ml-1">*</span>}
          </Label>
        )}
        
        <div className="relative">
          <Input
            ref={ref}
            id={id}
            name={name}
            type={showPassword ? 'text' : 'password'}
            placeholder={placeholder}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            required={required}
            disabled={disabled}
            className={className}
            {...props}
          />
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="absolute right-0 top-0 h-full px-3"
            onClick={() => setShowPassword(!showPassword)}
            disabled={disabled}
          >
            {showPassword ? (
              <EyeOff className="h-4 w-4" />
            ) : (
              <Eye className="h-4 w-4" />
            )}
          </Button>
        </div>

        {showStrengthMeter && value && (
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <Progress 
                value={(strength.score / 6) * 100} 
                className="flex-1 h-2" 
              />
              <span className={`text-xs font-medium ${
                strength.score <= 2 ? 'text-destructive' :
                strength.score <= 4 ? 'text-yellow-600' :
                'text-green-600'
              }`}>
                {getStrengthText(strength.score)}
              </span>
            </div>

            {strength.feedback.length > 0 && (
              <div className="space-y-1">
                {strength.feedback.slice(0, 3).map((feedback, index) => (
                  <div key={index} className="flex items-center gap-2 text-xs">
                    <X className="h-3 w-3 text-destructive flex-shrink-0" />
                    <span className="text-muted-foreground">{feedback}</span>
                  </div>
                ))}
              </div>
            )}

            {strength.isValid && (
              <div className="flex items-center gap-2 text-xs">
                <Check className="h-3 w-3 text-green-600 flex-shrink-0" />
                <span className="text-green-600">Password meets security requirements</span>
              </div>
            )}
          </div>
        )}
      </div>
    );
  }
);

PasswordInput.displayName = 'PasswordInput';